import { logEvent, setUserProperties, setUserId as firebaseSetUserId } from 'firebase/analytics'
import { getAnalyticsInstance } from '../config/firebase'

class AnalyticsService {
  static analyticsReady = false
  static pendingEvents = []
  static safetyTimeoutSet = false

  static markReady = () => {
    if (AnalyticsService.analyticsReady) return
    AnalyticsService.analyticsReady = true
    const queued = [...AnalyticsService.pendingEvents]
    AnalyticsService.pendingEvents = []
    queued.forEach(({ name, params, options }) => {
      AnalyticsService.trackEvent(name, params, options)
    })
  }

  static ensureReady = () => {
    if (AnalyticsService.safetyTimeoutSet) return
    AnalyticsService.safetyTimeoutSet = true

    setTimeout(() => {
      if (!AnalyticsService.analyticsReady) {
        console.warn('[Analytics] Auto-enabling after timeout')
        AnalyticsService.markReady()
      }
    }, 3000)
  }

  static sanitizeEventName = (eventName) => {
    if (!eventName || typeof eventName !== 'string') return 'invalid_event'

    let sanitized = eventName
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')

    if (sanitized.length > 40) sanitized = sanitized.substring(0, 40)
    return sanitized || 'invalid_event'
  }

  static trackEvent = (eventName, parameters = {}, options = {}) => {
    try {
      AnalyticsService.ensureReady()

      if (!AnalyticsService.analyticsReady) {
        AnalyticsService.pendingEvents.push({ name: eventName, params: parameters, options })
        return
      }

      const {
        includeTimestamp = true,
        logToConsole = true,
        customPrefix = 'Analytics',
      } = options

      const sanitizedEventName = AnalyticsService.sanitizeEventName(eventName)

      const eventData = {
        ...parameters,
        device_id: AnalyticsService.deviceId,
        platform: 'web',
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        referrer: document.referrer || undefined,
        ...(import.meta.env.DEV && { debug_mode: true }),
      }

      if (includeTimestamp) {
        eventData.timestamp = new Date().toISOString()
      }

      if (logToConsole) {
        const suffix = sanitizedEventName !== eventName ? ` (sanitized from: ${eventName})` : ''
        // console.log(`[${customPrefix}] ${sanitizedEventName}${suffix}`, eventData)
      }

      const analytics = getAnalyticsInstance()
      if (!analytics) {
        console.warn('[Analytics] Firebase not available — event logged locally only')
        return
      }

      logEvent(analytics, sanitizedEventName, eventData)
    } catch (error) {
      console.error('[Analytics] trackEvent error:', error)
    }
  }

  // --- Screen tracking ---

  static trackScreenView = (screenName, params = {}) => {
    AnalyticsService.trackEvent('screen_view', {
      screen_name: screenName,
      ...params,
    })
  }

  static trackScreenExit = (screenName, params = {}) => {
    AnalyticsService.trackEvent('screen_exit', {
      screen_name: screenName,
      ...params,
    })
  }

  // --- Button / CTA tracking ---

  static trackButtonClick = (buttonName, params = {}) => {
    AnalyticsService.trackEvent('button_click', {
      button_name: buttonName,
      ...params,
    })
  }

  // --- User properties ---

  static setUserProperty = (property, value) => {
    try {
      const analytics = getAnalyticsInstance()
      if (!analytics) return
      setUserProperties(analytics, { [property]: value })
    } catch (error) {
      console.error('[Analytics] setUserProperty error:', error)
    }
  }

  static setUserId = (userId) => {
    try {
      const analytics = getAnalyticsInstance()
      if (!analytics) return
      firebaseSetUserId(analytics, userId)
    } catch (error) {
      console.error('[Analytics] setUserId error:', error)
    }
  }
}

export default AnalyticsService
